/**
 * KTAS 중증도분류 수행결과 — 카드·확대뷰.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';
import { TriageResultShell } from './TriageResultShell';
import { KtasVitalMonitor } from './KtasVitalMonitor';
import type { WinnerEntry } from './Top3Winners';
import { isTriageResponseCorrect } from './simulationResultBlocks';
import {
    type TriagePatientRow,
    type TriageViewMode,
    type KtasRateBarLevelKey,
    KTAS_RATE_BAR_ORDER,
    displayCell,
    formatKtasLevelDisplay,
    getTriageStageAnswers,
    ktasLevelCapsuleStyle,
    ktasRateFromSaltByColor,
    resolveKtasDisplayLevelKey,
} from './triageShared';

function ktasBarLevelRaw(level: KtasRateBarLevelKey): string {
    if (level === '미분류') return '미분류';
    return String(level);
}

export interface KtasTriageResultTableProps {
    title: string;
    patients: TriagePatientRow[];
    viewMode?: TriageViewMode;
    onViewModeChange?: (mode: TriageViewMode) => void;
    isAllParticipantsView?: boolean;
    winners?: WinnerEntry[];
}

const KtasEnlargedView: React.FC<{
    row: TriagePatientRow;
    isAllParticipantsView?: boolean;
}> = ({ row, isAllParticipantsView }) => {
    const { t } = useTranslation('class');
    const { firstCorrect, firstUser } = getTriageStageAnswers(row);
    // TODO: API 연결 미흡 - 정답 레벨 필드명이 불확실하여 여러 키를 시도
    const correctKtasLevelRaw = String(row.correctKtasLevel ?? row.correctAnswer ?? firstCorrect ?? '');
    const correctKtasDisplay = formatKtasLevelDisplay(correctKtasLevelRaw, t);
    const cell = 'px-3 py-2';
    const labelCell = `${cell} bg-gray-50 font-medium text-gray-600 w-36 align-top whitespace-nowrap`;
    const valueCell = `${cell} text-gray-900 min-w-0`;
    const byColor = row.correctRateByColor;
    const correctLevelKey = resolveKtasDisplayLevelKey(String(row.correctAnswer ?? firstCorrect ?? ''));

    const rateBars =
        isAllParticipantsView && byColor ? (
            <div className="space-y-3">
                <div className="text-base font-medium text-gray-500 mb-2">{t('simulationResults.ktasRateByLevelTitle')}</div>
                {KTAS_RATE_BAR_ORDER.map((level) => {
                    const value = ktasRateFromSaltByColor(byColor, level);
                    const raw = ktasBarLevelRaw(level);
                    const label = formatKtasLevelDisplay(raw, t);
                    const isCorrectAnswer = correctLevelKey === level;
                    const barClass = ktasLevelCapsuleStyle(raw);
                    return (
                        <div key={String(level)} className="flex items-center gap-4">
                            <span className={`text-sm font-semibold min-w-[5.5rem] text-center rounded-lg px-3 py-2 ${barClass}`}>
                                {label}
                            </span>
                            <div className="flex-1 min-w-0 h-3 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${barClass}`} style={{ width: `${value}%` }} />
                            </div>
                            <div className="flex items-center gap-3 shrink-0 w-28 justify-start">
                                <span className="text-sm font-medium text-gray-900">{value}%</span>
                                {isCorrectAnswer && (
                                    <span className="flex items-center justify-center gap-1">
                                        <CheckCircle2 size={28} className="text-brand-500 rounded-full" />
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        ) : null;

    return (
        <div className="space-y-6 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            {rateBars}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,0.36fr)_minmax(0,0.64fr)]">
                <div className="min-w-0 space-y-4">
                    <div className="text-xl font-bold text-gray-900">
                        {row.patientName} {row.gender} {row.age}
                    </div>
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                        <tbody className="divide-y divide-gray-200">
                            <tr>
                                <td className={labelCell}>{t('simulationResults.ktasPastHistory')}</td>
                                <td className={valueCell}>{displayCell(row.pastHistory)}</td>
                            </tr>
                            <tr>
                                <td className={labelCell}>{t('simulationResults.ktasVisitMotivation')}</td>
                                <td className={valueCell}>{displayCell(row.visitMotivation)}</td>
                            </tr>
                            <tr>
                                <td className={labelCell}>{t('simulationResults.ktasConsciousness')}</td>
                                <td className={valueCell}>{displayCell(row.consciousnessLevel)}</td>
                            </tr>
                        </tbody>
                    </table>
                    <KtasVitalMonitor row={row} />
                </div>
                <div className="flex flex-col gap-6 min-w-0">
                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <div className="px-3 py-2 bg-gray-100 text-sm font-semibold text-gray-800">
                            {t('simulationResults.ktasAnswerSection')}
                        </div>
                        <table className="w-full text-sm">
                            <tbody className="divide-y divide-gray-200">
                                <tr>
                                    <td className={labelCell}>{t('simulationResults.ktasGcs')}</td>
                                    <td className={valueCell}>{displayCell(row.answerGcs)}</td>
                                </tr>
                                <tr>
                                    <td className={labelCell}>{t('simulationResults.ktasMainSymptom')}</td>
                                    <td className={valueCell}>{displayCell(row.answerMainSymptom)}</td>
                                </tr>
                                <tr>
                                    <td className={labelCell}>{t('simulationResults.ktasPathway')}</td>
                                    <td className={valueCell}>{displayCell(row.answerPathway)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <div className="px-3 py-2 bg-gray-100 text-sm font-semibold text-gray-800">
                            {t('simulationResults.ktasCorrectSection')}
                        </div>
                        <table className="w-full text-sm">
                            <tbody className="divide-y divide-gray-200">
                                <tr>
                                    <td className={labelCell}>{t('simulationResults.ktasGcs')}</td>
                                    <td className={valueCell}>{displayCell(row.correctGcs)}</td>
                                </tr>
                                <tr>
                                    <td className={labelCell}>{t('simulationResults.ktasMainSymptom')}</td>
                                    <td className={valueCell}>{displayCell(row.correctMainSymptom)}</td>
                                </tr>
                                <tr>
                                    <td className={labelCell}>{t('simulationResults.ktasKtasClassification')}</td>
                                    <td className={valueCell}>
                                        <span
                                            className={`inline-flex min-h-[32px] items-center justify-center rounded-lg px-3 py-1.5 text-sm font-bold ${ktasLevelCapsuleStyle(correctKtasLevelRaw)}`}
                                        >
                                            {correctKtasDisplay}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={labelCell}>{t('simulationResults.ktasPathway')}</td>
                                    <td className={valueCell}>{displayCell(row.correctPathway)}</td>
                                </tr>
                                <tr>
                                    <td className={labelCell}>{t('simulationResults.ktasComment')}</td>
                                    <td className={`${valueCell} whitespace-pre-wrap`}>{displayCell(row.comments)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const KtasPatientCard: React.FC<{
    row: TriagePatientRow;
    idx: number;
    compact?: boolean;
    isAllParticipantsView?: boolean;
}> = ({ row, compact, isAllParticipantsView }) => {
    const { t } = useTranslation('class');
    const { firstCorrect, firstUser } = getTriageStageAnswers(row);
    const hasFirstUser = firstUser != null && String(firstUser).trim() !== '';
    const isCorrect = isTriageResponseCorrect(firstCorrect, firstUser);
    const showCorrectRate = isAllParticipantsView && row.correctRate != null;
    const rate = Math.min(100, Math.max(0, row.correctRate ?? 0));
    const capsule = (raw: string | number) => ktasLevelCapsuleStyle(String(raw));

    return (
        <div className={`bg-white rounded-xl border border-gray-200/90 shadow-sm relative ${compact ? 'p-3' : 'p-4'}`}>
            <div className={compact ? 'absolute top-2 right-2' : 'absolute top-3 right-3'}>
                {isCorrect ? (
                    <CheckCircle2 size={compact ? 18 : 24} className="text-green-600 shrink-0" />
                ) : (
                    <XCircle size={compact ? 18 : 24} className="text-red-600 shrink-1/2" />
                )}
            </div>
            <div className={`font-semibold text-gray-900 mb-2 ${compact ? 'text-sm pr-14' : 'text-base pr-20'}`}>
                {row.patientName} <span className="text-gray-600"> - {row.gender} {row.age}</span>
            </div>
            <div className={compact ? 'space-y-1 text-sm' : 'space-y-2 text-sm'}>
                {!isAllParticipantsView && (
                    <div
                        className={`grid grid-cols-[auto_minmax(0,1fr)] min-w-0 ${compact ? 'gap-x-2 gap-y-1.5 text-xs' : 'gap-x-3 gap-y-2 text-sm'} items-center`}
                    >
                        <div className={compact ? 'min-w-[1.75rem]' : 'min-w-9'} aria-hidden />
                        <div className={`font-semibold text-gray-700 text-center ${compact ? 'text-xs' : 'text-sm'}`}>{t('categoryTriage')}</div>

                        <div className={`text-gray-500 shrink-0 ${compact ? 'text-xs' : 'text-sm'}`}>
                            {t('simulationResults.ktasRowCorrect')}
                        </div>
                        <span className={`inline-block rounded-full font-semibold text-center justify-self-stretch w-full ${capsule(firstCorrect)} ${compact ? 'px-2 py-0.5' : 'px-3 py-1'}`}>
                            {formatKtasLevelDisplay(String(firstCorrect), t)}
                        </span>

                        <div className={`text-gray-500 shrink-0 ${compact ? 'text-xs' : 'text-sm'}`}>
                            {t('simulationResults.ktasRowUser')}
                        </div>
                        {hasFirstUser ? (
                            <span className={`inline-block rounded-full font-semibold text-center justify-self-stretch w-full ${capsule(firstUser)} ${compact ? 'px-2 py-0.5' : 'px-3 py-1'}`}>
                                {formatKtasLevelDisplay(String(firstUser), t)}
                            </span>
                        ) : (
                            <span className={`inline-block rounded-full font-semibold text-center justify-self-stretch w-full ${capsule('미분류')} ${compact ? 'px-2 py-0.5' : 'px-3 py-1'}`}>
                                {t('simulationResults.ktasUnclassified')}
                            </span>
                        )}
                    </div>
                )}
                {showCorrectRate && (
                    <div className="flex items-center gap-2 pt-1">
                        <span className={`text-gray-500 shrink-0 ${compact ? 'w-14 text-xs' : 'w-20'}`}>
                            {t('simulationResults.colSuccessRate')}
                        </span>
                        <div className="flex-1 min-w-0 flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${capsule(row.correctAnswer)}`}
                                    style={{ width: `${rate}%` }}
                                />
                            </div>
                            <span className="text-sm font-medium text-gray-900 shrink-0 w-8">{rate}%</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const KtasTriageResultTable: React.FC<KtasTriageResultTableProps> = (props) => {
    const { title, patients, viewMode, onViewModeChange, isAllParticipantsView, winners } = props;
    return (
        <TriageResultShell
            title={title}
            patients={patients}
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            isAllParticipantsView={isAllParticipantsView}
            winners={winners}
            noPatientLayout="ktas"
            renderCard={(row, idx) => (
                <KtasPatientCard key={idx} row={row} idx={idx} compact={false} isAllParticipantsView={isAllParticipantsView} />
            )}
            renderEnlarged={(row) => <KtasEnlargedView row={row} isAllParticipantsView={isAllParticipantsView} />}
        />
    );
};
